import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Copy, Check, ArrowRightLeft } from '../../components/icons';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import Breadcrumb from '../../components/Breadcrumb';
import {
  rgbToHex,
  hexToRgb,
  rgbToHsl,
  hslToRgb,
  rgbToHsv,
  hsvToRgb,
  rgbToCmyk,
  cmykToRgb,
  rgbToOklch,
  oklchToRgb,
  getNearestColorName,
} from '../../utils/colors';
import type { RGB } from '../../types';
import { syncRouteUrlMetadata } from '../../utils/seo';
import { copyResult } from '../../utils/analytics';

// ── SEO ────────────────────────────────────────────────────────────

const BASE_URL = 'https://color-match.io';

export const CONVERTER_FAQS = [
  {
    question: 'How do I convert HEX to RGB?',
    answer: 'Read each pair of HEX digits as a base-16 number. For #FF6B6B, FF is 255 and 6B is 107, so the result is rgb(255, 107, 107). For three-digit notation such as #F6A, repeat each digit first: #FF66AA becomes rgb(255, 102, 170).',
  },
  {
    question: 'What is the difference between HSL and HSV?',
    answer: 'Both use hue and saturation, but HSL lightness is the average of the largest and smallest RGB channels, while HSV value is the largest channel. For pure red, HSL lightness is 50% and HSV value is 100%. A value of 100% does not mean white; saturation still matters.',
  },
  {
    question: 'When should I use CMYK vs RGB?',
    answer: 'Use RGB for screen colors. CMYK describes cyan, magenta, yellow and black ink values for print workflows. The CMYK values in this converter are a mathematical approximation without an ICC profile. Ask your printer for the required profile and proof the result when matching printed colors matters.',
  },
];

const updateConverterMetadata = () => {
  if (typeof document === 'undefined') return;

  const title = 'Color Converter — HEX, RGB, HSL, CMYK, HSV, OKLCH | Color Match';
  const description =
    'Free online color converter. Instantly convert between HEX, RGB, HSL, HSV, CMYK, and OKLCH color formats. Copy values with one click.';
  const canonicalUrl = `${BASE_URL}/converter`;

  document.title = title;

  const setMeta = (attr: string, key: string, value: string) => {
    let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLElement | null;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute('content', value);
  };

  const setLink = (rel: string, attr: string, value: string) => {
    let el = document.querySelector(`link[rel="${rel}"][${attr}]`) as HTMLElement | null;
    if (!el) {
      el = document.querySelector(`link[rel="${rel}"]`) as HTMLElement | null;
    }
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', rel);
      document.head.appendChild(el);
    }
    el.setAttribute('href', value);
  };

  setLink('canonical', 'href', canonicalUrl);
  setMeta('name', 'description', description);
  setMeta('name', 'keywords', 'color converter, hex to rgb, rgb to hex, hex to hsl, rgb to cmyk, color format converter, oklch converter, hsv converter');
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:url', canonicalUrl);
  setMeta('property', 'og:type', 'website');

  // JSON-LD
  let script = document.getElementById('page-jsonld') as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = 'page-jsonld';
    script.setAttribute('type', 'application/ld+json');
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebApplication',
        name: 'Color Converter — Color Match',
        applicationCategory: 'DesignApplication',
        operatingSystem: 'Web',
        url: canonicalUrl,
        description,
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: CONVERTER_FAQS.map(({ question, answer }) => ({
          '@type': 'Question',
          name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
          { '@type': 'ListItem', position: 2, name: 'Color Converter', item: canonicalUrl },
        ],
      },
    ],
  });
  syncRouteUrlMetadata();
};

// ── Types ──────────────────────────────────────────────────────────

type ColorFormat = 'hex' | 'rgb' | 'hsl' | 'hsv' | 'cmyk' | 'oklch';

interface FormatConfig {
  label: string;
  fields: string[];
  ranges: [number, number][];
  units?: string[];
}

const FORMAT_CONFIG: Record<ColorFormat, FormatConfig> = {
  hex: { label: 'HEX', fields: ['hex'], ranges: [[0, 0]], units: [] },
  rgb: { label: 'RGB', fields: ['R', 'G', 'B'], ranges: [[0, 255], [0, 255], [0, 255]] },
  hsl: { label: 'HSL', fields: ['H', 'S', 'L'], ranges: [[0, 360], [0, 100], [0, 100]], units: ['°', '%', '%'] },
  hsv: { label: 'HSV', fields: ['H', 'S', 'V'], ranges: [[0, 360], [0, 100], [0, 100]], units: ['°', '%', '%'] },
  cmyk: { label: 'CMYK', fields: ['C', 'M', 'Y', 'K'], ranges: [[0, 100], [0, 100], [0, 100], [0, 100]], units: ['%', '%', '%', '%'] },
  oklch: { label: 'OKLCH', fields: ['L', 'C', 'H'], ranges: [[0, 100], [0, 0.4], [0, 360]], units: ['%', '', '°'] },
};

// ── Component ─────────────────────────────────────────────────────

const ColorConverterPage: React.FC = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const queryHex = new URLSearchParams(search).get('hex')?.replace(/^#/, '');
  const initialHex = queryHex && /^[0-9a-f]{6}$/i.test(queryHex) ? queryHex.toUpperCase() : '6366F1';
  const [rgb, setRgb] = useState<RGB>(() => hexToRgb(initialHex)!);
  const [hexInput, setHexInput] = useState(initialHex);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [copyError, setCopyError] = useState('');

  useEffect(() => {
    setRgb(hexToRgb(initialHex)!);
    setHexInput(initialHex);
  }, [initialHex]);

  useEffect(() => {
    updateConverterMetadata();
  }, []);

  const updateFromRgb = useCallback((newRgb: RGB) => {
    const clamped: RGB = {
      r: Math.max(0, Math.min(255, Math.round(newRgb.r))),
      g: Math.max(0, Math.min(255, Math.round(newRgb.g))),
      b: Math.max(0, Math.min(255, Math.round(newRgb.b))),
    };
    setRgb(clamped);
    const value = rgbToHex(clamped.r, clamped.g, clamped.b).replace('#', '');
    setHexInput(value);
    navigate('/converter?hex=' + value, { replace: true });
  }, [navigate]);

  const handleHexChange = (value: string) => {
    const clean = value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
    setHexInput(clean);
    if (clean.length === 6) {
      const parsed = hexToRgb(clean);
      if (parsed) {
        setRgb(parsed);
        navigate('/converter?hex=' + clean.toUpperCase(), { replace: true });
      }
    }
  };

  const handleRgbChange = (channel: keyof RGB, value: number) => {
    updateFromRgb({ ...rgb, [channel]: value });
  };

  const handleHslChange = (h: number, s: number, l: number) => {
    const newRgb = hslToRgb(h, s, l);
    updateFromRgb(newRgb);
  };

  const handleHsvChange = (h: number, s: number, v: number) => {
    const newRgb = hsvToRgb(h, s, v);
    updateFromRgb(newRgb);
  };

  const handleCmykChange = (c: number, m: number, y: number, k: number) => {
    const newRgb = cmykToRgb(c, m, y, k);
    updateFromRgb(newRgb);
  };

  const handleOklchChange = (l: number, c: number, h: number) => {
    const newRgb = oklchToRgb(l, c * 100, h);
    updateFromRgb(newRgb);
  };

  // Computed values
  const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
  const cmyk = rgbToCmyk(rgb.r, rgb.g, rgb.b);
  const oklch = rgbToOklch(rgb.r, rgb.g, rgb.b);
  const colorName = getNearestColorName(rgb.r, rgb.g, rgb.b);

  const copyToClipboard = async (text: string, label: string) => {
    const ok = await copyResult(text, 'converter', 'copy', label);
    setCopyError(ok ? '' : 'Clipboard unavailable. Select the displayed value to copy it.');
    setCopiedField(ok ? label : null);
    if (ok) setTimeout(() => setCopiedField(null), 1500);
  };

  const formatValues: Record<ColorFormat, string> = {
    hex: hex,
    rgb: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
    hsl: `hsl(${Math.round(hsl.h)}, ${Math.round(hsl.s)}%, ${Math.round(hsl.l)}%)`,
    hsv: `hsv(${Math.round(hsv.h)}, ${Math.round(hsv.s)}%, ${Math.round(hsv.v)}%)`,
    cmyk: `cmyk(${Math.round(cmyk.c)}%, ${Math.round(cmyk.m)}%, ${Math.round(cmyk.y)}%, ${Math.round(cmyk.k)}%)`,
    oklch: `oklch(${oklch.l.toFixed(1)}% ${(oklch.c / 100).toFixed(3)} ${Math.round(oklch.h)})`,
  };

  const CopyButton: React.FC<{ format: ColorFormat }> = ({ format }) => {
    const isCopied = copiedField === format;
    return (
      <button
        onClick={() => copyToClipboard(formatValues[format], format)}
        className="ui-button-secondary"
        title={`Copy ${FORMAT_CONFIG[format].label} value`}
      >
        {isCopied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
        {isCopied ? 'Copied' : 'Copy'}
      </button>
    );
  };

  const NumberInput: React.FC<{
    label: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    unit?: string;
    onChange: (v: number) => void;
  }> = ({ label, value, min, max, step = 1, unit, onChange }) => (
    <div className="flex items-center gap-2">
      <label className="w-6 text-sm font-semibold text-ink-secondary shrink-0">{label}</label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        aria-label={label + " slider"}
        className="min-w-0 flex-1 accent-brand-600"
      />
      <div className="flex items-center gap-0.5 w-16 shrink-0">
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={step < 1 ? value.toFixed(3) : Math.round(value)}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          aria-label={label + " value"}
          className="ui-input min-h-9 w-full px-1 text-center font-mono"
        />
        {unit && <span className="text-xs text-ink-muted">{unit}</span>}
      </div>
    </div>
  );

  return (
    <Layout maxWidth="max-w-5xl">
      <div className="space-y-8">
        <Breadcrumb items={[{ label: 'Color Converter' }]} />

        {/* Header */}
        <PageHeader icon={ArrowRightLeft} title="Color Converter" description="Convert colors instantly between HEX, RGB, HSL, HSV, CMYK, and OKLCH. Enter a value in any format to see all conversions." />
        <p role="status" className="text-sm text-red-700">{copyError}</p>

        {/* Color Preview */}
        <div className="ui-panel p-6">
          <div className="flex flex-col md:flex-row gap-6 items-center">
            <div
              className="w-full md:w-48 h-32 rounded-xl shadow-inner border border-surface-border"
              style={{ backgroundColor: hex }}
            />
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl font-bold text-ink">{colorName}</h2>
              <p className="text-lg font-mono text-ink-muted mt-1">{hex}</p>
              <a
                href={`/color/${hex.replace('#', '')}`}
                className="inline-flex items-center gap-1.5 mt-3 text-sm text-brand-600 hover:text-brand-700 font-medium"
              >
                <ArrowRightLeft className="w-4 h-4" />
                View full color details
              </a>
            </div>
          </div>
        </div>

        {/* Converter Cards */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* HEX */}
          <div className="ui-panel p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-ink-secondary">HEX</h3>
              <CopyButton format="hex" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-ink-muted font-mono text-lg">#</span>
              <input
                type="text"
                value={hexInput}
                onChange={(e) => handleHexChange(e.target.value)}
                maxLength={6}
                className="flex-1 text-lg font-mono bg-surface border border-surface-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent uppercase"
                placeholder="FF6B6B"
              />
            </div>
          </div>

          {/* RGB */}
          <div className="ui-panel p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-ink-secondary">RGB</h3>
              <CopyButton format="rgb" />
            </div>
            <div className="space-y-2">
              <NumberInput label="R" value={rgb.r} min={0} max={255} onChange={(v) => handleRgbChange('r', v)} />
              <NumberInput label="G" value={rgb.g} min={0} max={255} onChange={(v) => handleRgbChange('g', v)} />
              <NumberInput label="B" value={rgb.b} min={0} max={255} onChange={(v) => handleRgbChange('b', v)} />
            </div>
          </div>

          {/* HSL */}
          <div className="ui-panel p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-ink-secondary">HSL</h3>
              <CopyButton format="hsl" />
            </div>
            <div className="space-y-2">
              <NumberInput label="H" value={Math.round(hsl.h)} min={0} max={360} unit="°" onChange={(h) => handleHslChange(h, hsl.s, hsl.l)} />
              <NumberInput label="S" value={Math.round(hsl.s)} min={0} max={100} unit="%" onChange={(s) => handleHslChange(hsl.h, s, hsl.l)} />
              <NumberInput label="L" value={Math.round(hsl.l)} min={0} max={100} unit="%" onChange={(l) => handleHslChange(hsl.h, hsl.s, l)} />
            </div>
          </div>

          {/* HSV */}
          <div className="ui-panel p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-ink-secondary">HSV</h3>
              <CopyButton format="hsv" />
            </div>
            <div className="space-y-2">
              <NumberInput label="H" value={Math.round(hsv.h)} min={0} max={360} unit="°" onChange={(h) => handleHsvChange(h, hsv.s, hsv.v)} />
              <NumberInput label="S" value={Math.round(hsv.s)} min={0} max={100} unit="%" onChange={(s) => handleHsvChange(hsv.h, s, hsv.v)} />
              <NumberInput label="V" value={Math.round(hsv.v)} min={0} max={100} unit="%" onChange={(v) => handleHsvChange(hsv.h, hsv.s, v)} />
            </div>
          </div>

          {/* CMYK */}
          <div className="ui-panel p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-ink-secondary">CMYK</h3>
              <CopyButton format="cmyk" />
            </div>
            <div className="space-y-2">
              <NumberInput label="C" value={Math.round(cmyk.c)} min={0} max={100} unit="%" onChange={(c) => handleCmykChange(c, cmyk.m, cmyk.y, cmyk.k)} />
              <NumberInput label="M" value={Math.round(cmyk.m)} min={0} max={100} unit="%" onChange={(m) => handleCmykChange(cmyk.c, m, cmyk.y, cmyk.k)} />
              <NumberInput label="Y" value={Math.round(cmyk.y)} min={0} max={100} unit="%" onChange={(y) => handleCmykChange(cmyk.c, cmyk.m, y, cmyk.k)} />
              <NumberInput label="K" value={Math.round(cmyk.k)} min={0} max={100} unit="%" onChange={(k) => handleCmykChange(cmyk.c, cmyk.m, cmyk.y, k)} />
            </div>
          </div>

          {/* OKLCH */}
          <div className="ui-panel p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-ink-secondary">OKLCH</h3>
              <CopyButton format="oklch" />
            </div>
            <div className="space-y-2">
              <NumberInput label="L" value={parseFloat(oklch.l.toFixed(1))} min={0} max={100} step={0.1} unit="%" onChange={(l) => handleOklchChange(l, oklch.c / 100, oklch.h)} />
              <NumberInput label="C" value={parseFloat((oklch.c / 100).toFixed(3))} min={0} max={0.4} step={0.001} onChange={(c) => handleOklchChange(oklch.l, c, oklch.h)} />
              <NumberInput label="H" value={Math.round(oklch.h)} min={0} max={360} unit="°" onChange={(h) => handleOklchChange(oklch.l, oklch.c / 100, h)} />
            </div>
          </div>
        </div>

        {/* Quick Copy Section */}
        <div className="ui-panel p-6">
          <h3 className="font-semibold text-ink-secondary mb-4">Quick Copy — All Formats</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(Object.keys(formatValues) as ColorFormat[]).map((format) => (
              <button
                key={format}
                onClick={() => copyToClipboard(formatValues[format], `quick-${format}`)}
                className="flex items-center justify-between px-4 py-3 bg-surface hover:bg-surface-sunken rounded-lg border border-surface-border transition-colors group"
              >
                <span className="text-xs font-semibold text-ink-muted uppercase">{FORMAT_CONFIG[format].label}</span>
                <span className="font-mono text-sm text-ink-secondary truncate ml-3">{formatValues[format]}</span>
                {copiedField === `quick-${format}` ? (
                  <Check className="w-4 h-4 text-green-600 ml-2 shrink-0" />
                ) : (
                  <Copy className="w-4 h-4 text-ink-faint group-hover:text-ink-muted ml-2 shrink-0" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* SEO Content */}
        <section className="ui-panel p-6 md:p-8">
          <h2 className="text-xl font-bold text-ink mb-4">How Color Conversion Works</h2>
          <div className="prose prose-slate max-w-none text-sm leading-relaxed space-y-4">
            <p>
              Colors can be represented in many formats depending on the medium. <strong>HEX</strong> codes
              like <code>#FF6B6B</code> are shorthand for RGB values used in CSS and web design.
              <strong> RGB</strong> (Red, Green, Blue) describes colors as light combinations for screens.
            </p>
            <p>
              <strong>HSL</strong> (Hue, Saturation, Lightness) is more intuitive for designers — adjusting
              saturation or lightness is straightforward. <strong>HSV</strong> (Hue, Saturation, Value) is
              similar but used in tools like Photoshop's color picker.
            </p>
            <p>
              <strong>CMYK</strong> (Cyan, Magenta, Yellow, Key/Black) is the standard for print production,
              where colors are created by subtracting light using inks. These CMYK values are an
              approximation without a print profile. <strong>OKLCH</strong> separates perceptual lightness,
              chroma and hue; check foreground and background contrast separately when creating a palette.
            </p>
            <p><a href="https://www.w3.org/TR/css-color-4/#hex-notation" className="text-brand-600 underline">W3C: HEX notation and sRGB colors</a> · <a href="/methodology" className="text-brand-600 underline">Conversion methods and limitations</a></p>
          </div>
        </section>
        <section className="ui-panel p-6 md:p-8" aria-labelledby="conversion-questions">
          <h2 id="conversion-questions" className="text-xl font-bold text-ink">Color conversion questions</h2>
          <div className="mt-5 space-y-6">{CONVERTER_FAQS.map(({ question, answer }) => <div key={question}>
            <h3 className="font-semibold text-ink">{question}</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink-secondary">{answer}</p>
          </div>)}</div>
        </section>
      </div>
    </Layout>
  );
};

export default ColorConverterPage;
